import React from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import SvgIcon from '@mui/material/SvgIcon';
import { ReactComponent as InfoIcon } from "assets/images/criticalIncident/info_icon.svg";
import WarningIcon from '@mui/icons-material/WarningRounded';
import { ReactComponent as ErrorIcon } from "assets/images/criticalIncident/exclamation_diamond.svg";
import './PriorityAlert.scss';

export type Priority = 'normal' | 'high' | 'critical';

interface PriorityAlertPropsBase {
    title?: string;
    priority: Priority;
    simple?: boolean;
    iconTooltip?: string;
}

interface PriorityAlertWithChildren extends PriorityAlertPropsBase {
    children: React.ReactNode;
    content?: never;
}

interface PriorityAlertWithContentProp extends PriorityAlertPropsBase {
    children?: never;
    content: string | React.ReactNode;
}

type PriorityAlertProps = PriorityAlertWithChildren | PriorityAlertWithContentProp;

const priorityConfig = {
    normal: {
        icon: <SvgIcon component={InfoIcon} inheritViewBox />,
        className: 'priority-normal',
        severity: 'info'
    },
    high: {
        icon: <SvgIcon component={WarningIcon} inheritViewBox />,
        className: 'priority-high',
        severity: 'warning'
    },
    critical: {
        icon: <SvgIcon component={ErrorIcon} inheritViewBox />,
        className: 'priority-critical',
        severity: 'error'
    }
} as const;

export const PriorityAlert: React.FC<PriorityAlertProps> = ({
    title,
    priority = 'normal',
    content = null,
    children = null,
    simple = false,
    iconTooltip = ''
}) => {
    const config = priorityConfig[priority];

    return (
        <Alert
            icon={iconTooltip ? <span data-tooltip-right={iconTooltip}>{config.icon}</span> : config.icon}
            severity={config.severity as any}
            className={`priority-alert ${config.className}${simple ? ' simple' : ''}`}
        >
            {title && <AlertTitle>{title}</AlertTitle>}
            {children ?? content}
        </Alert>
    );
};